// App shell + routes
import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import TripForm from "./components/TripForm";
import AuthModal from "./components/AuthModal";
import Upgrade from "./pages/Upgrade";
import Preview from "./pages/Preview";

const API_BASE = process.env.REACT_APP_API_URL || "";

function Header() {
  const { isLoggedIn, user, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="w-full flex items-center justify-end gap-3 px-4 py-2 bg-white border-b border-gray-100 text-sm">
        {isLoggedIn ? (
          <>
            <span className="text-gray-600">
              <span className="font-medium text-primary">{user.username}</span>
              {" · "}
              <span className="text-highlight font-semibold">{user.credits ?? 0} credits</span>
            </span>
            <button
              onClick={logout}
              className="px-3 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-background transition"
            >
              Log out
            </button>
          </>
        ) : (
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-1.5 rounded-lg bg-primary text-white font-medium hover:bg-accent transition"
          >
            Log in
          </button>
        )}
      </div>
      {showModal && <AuthModal onClose={() => setShowModal(false)} />}
    </>
  );
}

function Home() {
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submitToApi = async (formData) => {
    setErr(null);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/free-guide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const contentType = res.headers.get("content-type") || "";
      const raw = await res.text();
      if (!res.ok) {
        if (contentType.includes("application/json")) {
          let data;
          try { data = JSON.parse(raw); } catch {}
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        throw new Error(`HTTP ${res.status}: ${raw.slice(0, 200)}`);
      }
      if (!contentType.includes("application/json")) {
        throw new Error("Expected JSON but got something else.");
      }
      const data = JSON.parse(raw);
      // Persist immediately so pendingTrip survives a page refresh on /preview
      localStorage.setItem("pendingTrip", JSON.stringify({ formData, guideResult: data }));
      navigate("/preview", { state: { guide: data, formData } });
    } catch (error) {
      console.error(error);
      setErr(error.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-background px-4 py-6 app-root">
      <header className="text-center p-6">
        <Link to="/">
          <img src="/logo.png" alt="RouteMuse Logo" className="w-64 mx-auto mb-6" />
        </Link>
        <h1 className="text-3xl font-bold text-primary mb-4">Plan Your Trip</h1>
      </header>

      <TripForm onSubmit={submitToApi} />

      {err && <p className="text-red-600 text-sm">{err}</p>}
    </div>
  );
}

function AppLayout() {
  const [cookieConsent, setCookieConsent] = useState(
    localStorage.getItem('cookieConsent') || null
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/preview" element={<Preview />} />
        <Route path="/upgrade" element={<Upgrade />} />
      </Routes>
      <footer style={{
        textAlign: 'center',
        padding: '16px',
        fontSize: '12px',
        color: '#6b8fa8',
        borderTop: '1px solid #e5e7eb',
        marginTop: 'auto',
      }}>
        <a href="https://routemuse.me/terms-of-service/" target="_blank" rel="noreferrer" style={{color: '#6b8fa8', margin: '0 8px', textDecoration: 'none'}}>Terms of Service</a>
        <a href="https://routemuse.me/privacy-policy/" target="_blank" rel="noreferrer" style={{color: '#6b8fa8', margin: '0 8px', textDecoration: 'none'}}>Privacy Policy</a>
        <a href="https://routemuse.me/refund-policy/" target="_blank" rel="noreferrer" style={{color: '#6b8fa8', margin: '0 8px', textDecoration: 'none'}}>Refund Policy</a>
      </footer>
      {!cookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-primary
                        text-white px-6 py-4 flex flex-wrap items-center
                        justify-between gap-3 shadow-lg">
          <p className="text-sm max-w-xl leading-relaxed">
            We use cookies for authentication and analytics to improve
            your experience. See our{' '}
            <a
              href="https://routemuse.me/cookie-policy/"
              target="_blank"
              rel="noreferrer"
              className="underline text-highlight"
            >
              Cookie Policy
            </a>.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                localStorage.setItem('cookieConsent', 'accepted');
                setCookieConsent('accepted');
              }}
              className="bg-highlight text-white px-5 py-2 rounded-lg
                         text-sm font-semibold hover:opacity-90 transition"
            >
              Accept
            </button>
            <button
              onClick={() => {
                localStorage.setItem('cookieConsent', 'declined');
                setCookieConsent('declined');
              }}
              className="border border-white text-white px-5 py-2
                         rounded-lg text-sm hover:bg-white hover:text-primary
                         transition"
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
}
